import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import Logo from '../../components/brand/Logo.jsx'
import Button from '../../components/ui/Button.jsx'
import Icon from '../../components/ui/Icon.jsx'
import LanguageSwitch from '../../components/ui/LanguageSwitch.jsx'
import { Field, Input } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import api, { TOKEN_KEY, REFRESH_KEY, COMPANY_KEY } from '../../lib/api.js'
import { formatCurrency } from '../../lib/format.js'
import { passwordChecks, isValidPassword, passwordScore } from '../../lib/password.js'
import PaymentStep from './register/PaymentStep.jsx'
import DocumentsStep from './register/DocumentsStep.jsx'

/*
 * Registration wizard — full onboarding flow, faithfully mapped onto the backend
 * step machine (inventory §5). Each step advances the backend `current_step`:
 *
 *   info      POST /register                         -> pending_email  (+ auto-login)
 *   email     POST /register/verify-email            -> pending_terms
 *   terms     POST /register/accept-terms            -> pending_plan
 *   plan      POST /auth/register/select-plan        -> pending_payment
 *   payment   Stripe setup-intent + complete-payment -> pending_documents  (skipped if free)
 *   documents POST /register/upload-document (xN)     -> (stays pending_documents)
 *   confirm   POST /register/finalize                -> pending_validation -> waiting screen
 *
 * The wizard resumes at the right step from GET /register/status (a user who
 * logs back in mid-registration is routed here by LoginPage). Email + Stripe
 * require working SMTP/Stripe keys — validate the live happy path on staging.
 */
const STEPS = ['info', 'email', 'terms', 'plan', 'payment', 'documents', 'confirm']
const STEP_FOR = { pending_email: 'email', pending_terms: 'terms', pending_plan: 'plan', pending_payment: 'payment', pending_documents: 'documents' }
const LANGS = [['fr', 'Français'], ['en', 'English'], ['nl', 'Nederlands']]

export default function RegisterWizard() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { notify } = useFeedback()

  const [step, setStep] = useState('info')
  const [plans, setPlans] = useState([])
  const [planId, setPlanId] = useState(null)
  const [busy, setBusy] = useState(false)
  const [booting, setBooting] = useState(true)

  const [f, setF] = useState({ company_name: '', vat_digits: '', non_vat_liable: false, user_first_name: '', user_last_name: '', user_email: '', user_password: '', confirm: '', preferred_language: 'fr' })
  const [verifyCode, setVerifyCode] = useState('')
  const [terms, setTerms] = useState({ terms: false, privacy: false })
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }))
  const go = (s) => setStep(s)
  const selectedPlan = plans.find((p) => p.id === planId)

  // Load plans + resume from the backend step if already registered.
  useEffect(() => {
    api.get('/billing/plans/public').then(({ data }) => setPlans(data?.plans || [])).catch(() => {})
    const token = localStorage.getItem(TOKEN_KEY)
    if (!token) { setBooting(false); return }
    api.get('/register/status').then(({ data }) => {
      if (data?.current_step === 'pending_validation') { navigate('/app/company-pending-validation'); return }
      // Resume: the local form state is empty, so rehydrate the review fields
      // (company + user) from the backend so the confirmation step isn't blank.
      if (data?.company || data?.registering_user) {
        setF((s) => ({
          ...s,
          company_name: data.company?.name || s.company_name,
          vat_digits: (data.company?.vat_number || '').replace(/^BE/i, '') || s.vat_digits,
          user_email: data.registering_user?.email || s.user_email,
          user_first_name: data.registering_user?.first_name || s.user_first_name,
          user_last_name: data.registering_user?.last_name || s.user_last_name,
        }))
      }
      if (data?.selected_billing_plan_id) setPlanId(data.selected_billing_plan_id)
      if (STEP_FOR[data?.current_step]) go(STEP_FOR[data.current_step])
    }).catch(() => {}).finally(() => setBooting(false))
  }, [navigate])

  const pwChecks = passwordChecks(f.user_password)
  const pwMismatch = f.confirm && f.user_password !== f.confirm
  const step1Valid = f.company_name && /^\d{10}$/.test(f.vat_digits) && f.user_first_name && f.user_last_name && f.user_email && isValidPassword(f.user_password) && !pwMismatch

  // Login returns tokens in the body even with ONBOARDING_INCOMPLETE (403/200).
  const autoLogin = async () => {
    const read = (d) => {
      if (d?.access_token) localStorage.setItem(TOKEN_KEY, d.access_token)
      if (d?.refresh_token) localStorage.setItem(REFRESH_KEY, d.refresh_token)
      if (d?.onboarding?.company_id) localStorage.setItem(COMPANY_KEY, String(d.onboarding.company_id))
      return !!d?.access_token
    }
    try { const { data } = await api.post('/auth/login', { email: f.user_email, password: f.user_password }); if (!read(data)) throw new Error('no token') }
    catch (e) { if (!read(e?.response?.data)) throw e }
  }

  const err = (e, fallback) => notify({ tone: 'error', title: t('register.error'), message: e?.response?.data?.error || t(fallback) })

  const submitInfo = async () => {
    if (!step1Valid) return
    setBusy(true)
    try {
      await api.post('/register', {
        company_name: f.company_name, vat_number: `BE${f.vat_digits}`, non_vat_liable: f.non_vat_liable,
        user_email: f.user_email, user_password: f.user_password, user_first_name: f.user_first_name,
        user_last_name: f.user_last_name, preferred_language: f.preferred_language,
      })
      await autoLogin(); go('email')
    } catch (e) {
      const data = e?.response?.data
      if (data?.onboarding || (data?.error || '').includes('email')) { try { await autoLogin(); go('email'); return } catch { /* fall through */ } }
      err(e, 'register.errorGeneric')
    } finally { setBusy(false) }
  }

  const submitVerify = async () => {
    setBusy(true)
    try { await api.post('/register/verify-email', { code: verifyCode }); go('terms') }
    catch (e) { err(e, 'register.verify.invalid') } finally { setBusy(false) }
  }
  const resend = async () => {
    try { await api.post('/register/resend-verification'); notify({ tone: 'success', title: t('register.verify.resentTitle'), message: t('register.verify.resent') }) }
    catch (e) { err(e, 'register.errorGeneric') }
  }

  const submitTerms = async () => {
    if (!terms.terms || !terms.privacy) return
    setBusy(true)
    try { await api.post('/register/accept-terms', { accept_terms: true, accept_privacy: true, terms_version: '1.0' }); go('plan') }
    catch (e) { err(e, 'register.errorGeneric') } finally { setBusy(false) }
  }

  const submitPlan = async () => {
    if (!planId) return
    setBusy(true)
    try {
      await api.post('/auth/register/select-plan', { billing_plan_id: planId })
      go(selectedPlan?.is_free ? 'documents' : 'payment')
    } catch (e) { err(e, 'register.errorGeneric') } finally { setBusy(false) }
  }

  const finalize = async () => {
    setBusy(true)
    try { await api.post('/register/finalize', { billing_plan_id: planId }); navigate('/app/company-pending-validation') }
    catch (e) { err(e, 'register.errorGeneric') } finally { setBusy(false) }
  }

  // Some steps are skipped (free plan → no payment); compute the visible index.
  const visibleSteps = STEPS.filter((s) => s !== 'payment' || !selectedPlan?.is_free)
  const idx = visibleSteps.indexOf(step)

  return (
    <div style={{ minHeight: '100vh', padding: '40px 20px', background: 'radial-gradient(1200px 600px at 80% -10%,#efe9fb,#f6f4fb)' }}>
      <div style={{ position: 'absolute', top: 22, right: 22 }}><LanguageSwitch /></div>
      <div style={{ width: '100%', maxWidth: 680, margin: '0 auto' }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}><Link to="/"><Logo size={34} /></Link></div>

        <Progress t={t} step={step} idx={idx} total={visibleSteps.length} />

        <div style={{ background: '#fff', borderRadius: 'var(--radius-card-lg)', border: '1px solid var(--color-border)', boxShadow: 'var(--shadow-card)', padding: '30px 32px', animation: 'vfade .25s ease' }}>
          {booting ? <div style={{ textAlign: 'center', color: 'var(--color-text-muted)', padding: 20 }}>{t('common.loading')}</div>
            : step === 'info' ? <InfoStep t={t} f={f} set={set} pwChecks={pwChecks} pwScore={passwordScore(f.user_password)} pwMismatch={pwMismatch} valid={step1Valid} busy={busy} onSubmit={submitInfo} />
            : step === 'email' ? <EmailStep t={t} code={verifyCode} setCode={setVerifyCode} onSubmit={submitVerify} onResend={resend} busy={busy} email={f.user_email} />
            : step === 'terms' ? <TermsStep t={t} terms={terms} setTerms={setTerms} busy={busy} onSubmit={submitTerms} />
            : step === 'plan' ? <PlanStep t={t} plans={plans} planId={planId} setPlanId={setPlanId} onNext={submitPlan} busy={busy} />
            : step === 'payment' ? <PaymentStep t={t} plan={selectedPlan} onBack={() => go('plan')} onDone={() => go('documents')} />
            : step === 'documents' ? <DocumentsStep t={t} onBack={() => go(selectedPlan?.is_free ? 'plan' : 'payment')} onDone={() => go('confirm')} />
            : <ConfirmStep t={t} f={f} plan={selectedPlan} busy={busy} onBack={() => go('documents')} onFinalize={finalize} />}
        </div>

        <div style={{ textAlign: 'center', fontSize: 14, color: 'var(--color-text)', marginTop: 18 }}>
          {t('register.haveAccount')} <Link to="/login" style={{ fontWeight: 600, color: 'var(--color-primary)' }}>{t('landing.nav.login')}</Link>
        </div>
      </div>
    </div>
  )
}

function Progress({ t, step, idx, total }) {
  return (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <span style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 16, color: 'var(--color-ink)' }}>{t(`register.steps.${step}`)}</span>
        <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{t('register.stepCounter', { current: idx + 1, total })}</span>
      </div>
      <div style={{ height: 6, borderRadius: 4, background: 'var(--color-border)', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${((idx + 1) / total) * 100}%`, background: 'var(--color-primary)', transition: 'width .25s ease' }} />
      </div>
    </div>
  )
}

function SectionLabel({ children }) {
  return <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 15, color: 'var(--color-ink)', margin: '6px 0 12px' }}>{children}</h3>
}

function InfoStep({ t, f, set, pwChecks, pwScore, pwMismatch, valid, busy, onSubmit }) {
  const barColor = ['#e6e1f2', 'var(--color-danger)', 'var(--color-accent)', 'var(--color-success)'][pwScore]
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit() }}>
      <SectionLabel>{t('register.companySection')}</SectionLabel>
      <Field label={t('register.companyName')} required><Input value={f.company_name} onChange={(e) => set('company_name', e.target.value)} required /></Field>
      <Field label={f.non_vat_liable ? t('parties.fields.companyNumber') : t('parties.fields.vatNumber')} required>
        <div style={{ display: 'flex' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', padding: '0 14px', background: 'var(--color-bg-lilac-strong)', border: '1.5px solid var(--color-border-strong)', borderRight: 'none', borderRadius: 'var(--radius-field) 0 0 var(--radius-field)', fontWeight: 700, color: 'var(--color-ink)' }}>BE</span>
          <Input value={f.vat_digits} onChange={(e) => set('vat_digits', e.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="0123456789" inputMode="numeric" style={{ borderRadius: '0 var(--radius-field) var(--radius-field) 0' }} required />
        </div>
      </Field>
      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 9, cursor: 'pointer', fontSize: 13.5, marginBottom: 14 }}>
        <input type="checkbox" checked={f.non_vat_liable} onChange={(e) => set('non_vat_liable', e.target.checked)} /> {t('parties.fields.nonVatLiable')}
      </label>

      <SectionLabel>{t('register.userSection')}</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label={t('settings.profile.firstName')} required><Input value={f.user_first_name} onChange={(e) => set('user_first_name', e.target.value)} required /></Field>
        <Field label={t('settings.profile.lastName')} required><Input value={f.user_last_name} onChange={(e) => set('user_last_name', e.target.value)} required /></Field>
      </div>
      <Field label={t('parties.fields.email')} required><Input type="email" value={f.user_email} onChange={(e) => set('user_email', e.target.value)} required /></Field>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Field label={t('auth.login.password')} required><Input type="password" autoComplete="new-password" value={f.user_password} onChange={(e) => set('user_password', e.target.value)} required /></Field>
        <Field label={t('settings.password.confirm')} required error={pwMismatch ? t('settings.password.mismatch') : undefined}><Input type="password" autoComplete="new-password" value={f.confirm} onChange={(e) => set('confirm', e.target.value)} required style={pwMismatch ? { borderColor: 'var(--color-danger)' } : undefined} /></Field>
      </div>
      {f.user_password && (
        <div style={{ marginTop: -8, marginBottom: 14 }}>
          <div style={{ height: 5, borderRadius: 3, background: '#eee', overflow: 'hidden', marginBottom: 8 }}><div style={{ height: '100%', width: `${(pwScore / 3) * 100}%`, background: barColor }} /></div>
          <div style={{ display: 'flex', gap: 14, fontSize: 12 }}>
            {[['length', t('settings.password.ruleLength')], ['digit', t('settings.password.ruleDigit')], ['special', t('settings.password.ruleSpecial')]].map(([k, l]) => (
              <span key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: pwChecks[k] ? 'var(--color-success)' : 'var(--color-text-muted)' }}><Icon name={pwChecks[k] ? 'check' : 'x'} size={13} /> {l}</span>
            ))}
          </div>
        </div>
      )}
      <Field label={t('parties.fields.language')}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 }}>
          {LANGS.map(([l, label]) => (
            <button key={l} type="button" onClick={() => set('preferred_language', l)} style={{ height: 44, borderRadius: 'var(--radius-field)', fontSize: 13, fontWeight: 600, border: '1.5px solid ' + (f.preferred_language === l ? 'var(--color-primary)' : 'var(--color-border-strong)'), background: f.preferred_language === l ? 'var(--color-bg-lilac-strong)' : '#fff', color: f.preferred_language === l ? 'var(--color-primary)' : 'var(--color-text)' }}>{label}</button>
          ))}
        </div>
      </Field>
      <p style={{ fontSize: 12.5, color: 'var(--color-text-muted)', margin: '4px 0 16px' }}>{t('register.legalNote')}</p>
      <Button type="submit" variant="primary" icon="chevron-right" fullWidth loading={busy} disabled={!valid}>{t('register.continue')}</Button>
    </form>
  )
}

function EmailStep({ t, code, setCode, onSubmit, onResend, busy, email }) {
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit() }} style={{ textAlign: 'center' }}>
      <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--color-bg-lilac-strong)', color: 'var(--color-primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}><Icon name="mail" size={26} /></div>
      <h3 style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 19, color: 'var(--color-ink)', marginBottom: 6 }}>{t('register.verify.title')}</h3>
      <p style={{ color: 'var(--color-text)', fontSize: 14, marginBottom: 20 }}>{t('register.verify.subtitle', { email })}</p>
      <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="000000" inputMode="numeric" aria-label={t('register.verify.title')} style={{ textAlign: 'center', letterSpacing: '0.4em', fontSize: 20, fontWeight: 700, maxWidth: 220, margin: '0 auto 16px' }} />
      <Button type="submit" variant="primary" icon="check" fullWidth loading={busy} disabled={code.length !== 6}>{t('register.verify.submit')}</Button>
      <button type="button" onClick={onResend} style={{ background: 'none', border: 'none', color: 'var(--color-primary)', fontWeight: 600, fontSize: 13.5, marginTop: 14, cursor: 'pointer' }}>{t('register.verify.resendCta')}</button>
    </form>
  )
}

function TermsStep({ t, terms, setTerms, busy, onSubmit }) {
  const row = (k, label, to, linkLabel) => (
    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 14, marginBottom: 14, padding: 14, border: '1px solid var(--color-border)', borderRadius: 12 }}>
      <input type="checkbox" checked={terms[k]} onChange={(e) => setTerms((s) => ({ ...s, [k]: e.target.checked }))} style={{ marginTop: 3 }} />
      <span>{label} <Link to={to} target="_blank" style={{ color: 'var(--color-primary)', fontWeight: 600 }}>{linkLabel}</Link>.</span>
    </label>
  )
  return (
    <div>
      <SectionLabel>{t('register.termsTitle')}</SectionLabel>
      <p style={{ color: 'var(--color-text)', fontSize: 14, marginBottom: 18 }}>{t('register.termsIntro')}</p>
      {row('terms', t('register.acceptPrefix'), '/legal/cgu', t('register.terms'))}
      {row('privacy', t('register.acceptPrefix'), '/legal/privacy', t('register.privacy'))}
      <Button variant="primary" icon="chevron-right" fullWidth loading={busy} disabled={!terms.terms || !terms.privacy} onClick={onSubmit}>{t('register.continue')}</Button>
    </div>
  )
}

function PlanStep({ t, plans, planId, setPlanId, onNext, busy }) {
  const priceOf = (p) => (p.is_free ? 0 : (p.price ?? p.monthly_base_price))
  const features = Array.isArray(t('landing.pricing.features', { returnObjects: true })) ? t('landing.pricing.features', { returnObjects: true }) : []
  const visible = plans.filter((p) => p.is_active !== false && p.is_visible !== false)
  const single = visible.length === 1
  return (
    <div>
      <SectionLabel>{t('register.choosePlan')}</SectionLabel>
      <div style={{ display: 'grid', gridTemplateColumns: single ? '1fr' : 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, marginBottom: 20 }}>
        {visible.map((p) => {
          const on = p.id === planId
          const vol = p.price_per_outgoing_invoice || p.price_per_incoming_invoice
          return (
            <button key={p.id} type="button" onClick={() => setPlanId(p.id)} style={{ textAlign: 'left', padding: 20, borderRadius: 16, border: '1.5px solid ' + (on ? 'var(--color-primary)' : 'var(--color-border)'), background: on ? 'var(--color-bg-lilac-strong)' : '#fff', cursor: 'pointer' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                <span style={{ fontFamily: 'var(--font-title)', fontWeight: 700, fontSize: 17, color: 'var(--color-ink)' }}>{p.name}</span>
                <span style={{ width: 22, height: 22, borderRadius: '50%', border: '2px solid ' + (on ? 'var(--color-primary)' : 'var(--color-border-strong)'), background: on ? 'var(--color-primary)' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff' }}>{on && <Icon name="check" size={13} />}</span>
              </div>
              <div style={{ fontFamily: 'var(--font-title)', fontWeight: 800, fontSize: 26, color: 'var(--color-ink)' }}>{p.is_free ? t('register.free') : formatCurrency(priceOf(p))}{!p.is_free && <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--color-text-muted)' }}> / {p.billing_period === 'yearly' ? t('register.year') : t('register.month')} {t('subscription.htva')}</span>}</div>
              <p style={{ fontSize: 13, color: 'var(--color-text-muted)', margin: '4px 0 14px' }}>{p.description || t('landing.pricing.note')}</p>

              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-text)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 8 }}>{t('landing.pricing.includes')}</div>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 7, margin: 0, padding: 0 }}>
                <Feat>{t('landing.pricing.storage', { count: p.included_stored_invoices })}</Feat>
                {features.map((feat) => <Feat key={feat}>{feat}</Feat>)}
              </ul>

              {vol ? (
                <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed var(--color-border-strong)', display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 12.5, color: 'var(--color-text)' }}>
                  {p.price_per_outgoing_invoice ? <span><strong>{formatCurrency(p.price_per_outgoing_invoice)}</strong> {t('landing.pricing.volume.outgoing')}</span> : null}
                  {p.price_per_incoming_invoice ? <span><strong>{formatCurrency(p.price_per_incoming_invoice)}</strong> {t('landing.pricing.volume.incoming')}</span> : null}
                  {p.price_per_100_extra_invoices ? <span><strong>{formatCurrency(p.price_per_100_extra_invoices)}</strong> {t('landing.pricing.volume.extra')}</span> : null}
                </div>
              ) : null}
            </button>
          )
        })}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="primary" icon="chevron-right" disabled={!planId} loading={busy} onClick={onNext}>{t('register.continue')}</Button>
      </div>
    </div>
  )
}

function ConfirmStep({ t, f, plan, busy, onBack, onFinalize }) {
  return (
    <div>
      <SectionLabel>{t('register.confirmTitle')}</SectionLabel>
      <div style={{ background: 'var(--color-bg-lilac)', borderRadius: 12, padding: 18, marginBottom: 20 }}>
        <Row label={t('register.companyName')} value={f.company_name} />
        <Row label={t('parties.fields.vatNumber')} value={`BE${f.vat_digits}`} />
        <Row label={t('parties.fields.email')} value={f.user_email} />
        {plan && <Row label={t('register.steps.plan')} value={plan.name} />}
      </div>
      <p style={{ fontSize: 13.5, color: 'var(--color-text)', marginBottom: 20 }}>{t('register.confirmNote')}</p>
      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button variant="secondary" onClick={onBack}>{t('register.back')}</Button>
        <Button variant="primary" icon="check" loading={busy} onClick={onFinalize}>{t('register.finish')}</Button>
      </div>
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0' }}>
      <span style={{ fontSize: 13.5, color: 'var(--color-text)' }}>{label}</span>
      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-ink)' }}>{value}</span>
    </div>
  )
}

function Feat({ children }) {
  return (
    <li style={{ display: 'flex', gap: 8, fontSize: 13.5, color: 'var(--color-text)' }}>
      <span style={{ color: 'var(--color-success)', flexShrink: 0 }}><Icon name="check" size={16} /></span>{children}
    </li>
  )
}
